import axios from 'axios';
import {
  SendScoutMessageRequest,
  SendScoutMessageResponse,
  BulkSendScoutMessageRequest,
  BulkSendScoutMessageResponse,
  ScoutHistoryResponse,
  ScoutQuotaResponse,
} from '../types/scout-message';

const API_BASE_URL = '/api/scouts';

export const scoutMessageService = {
  // スカウトメッセージ送信
  async sendMessage(
    request: SendScoutMessageRequest
  ): Promise<SendScoutMessageResponse> {
    try {
      // companyIdをリクエストから取得してURLに含める
      const companyId = request.companyId || 9; // デフォルト企業ID（テスト用）
      // バックエンドのフィールド名に変換
      const backendRequest = {
        recipient_user_id: request.recipientUserId,
        subject: request.subject,
        message_body: request.messageBody,
      };

      const response = await axios.post(
        `${API_BASE_URL}/${companyId}`,
        backendRequest,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error?.message) {
          throw new Error(error.response.data.error.message);
        }
        switch (error.response?.status) {
          case 403:
            throw new Error('スカウト送信の権限がありません');
          case 400:
            if (error.response.data.error?.code === 'RECIPIENT_BLOCKS_SCOUTS') {
              throw new Error('この学生はスカウトメールを受信しない設定です');
            }
            throw new Error('送信内容に問題があります');
          case 429:
            throw new Error(
              '送信制限に達しています。しばらく時間をおいてから再度お試しください'
            );
          case 500:
            throw new Error('サーバーエラーが発生しました');
          default:
            throw new Error(
              `API エラー: ${error.response?.status || 'unknown'}`
            );
        }
      }
      throw new Error('スカウトメッセージの送信に失敗しました');
    }
  },

  // 一括スカウト送信
  async sendBulkMessage(
    request: BulkSendScoutMessageRequest
  ): Promise<BulkSendScoutMessageResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/bulk`, request, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error?.message) {
          throw new Error(error.response.data.error.message);
        }
        switch (error.response?.status) {
          case 403:
            throw new Error('一括スカウト送信の権限がありません');
          case 400:
            throw new Error('送信内容または受信者リストに問題があります');
          case 429:
            throw new Error('送信制限に達しています');
          case 500:
            throw new Error('サーバーエラーが発生しました');
          default:
            throw new Error(
              `API エラー: ${error.response?.status || 'unknown'}`
            );
        }
      }
      throw new Error('一括スカウトメッセージの送信に失敗しました');
    }
  },

  // 送信履歴取得
  async getHistory(
    companyId: number = 9,
    page: number = 1
  ): Promise<ScoutHistoryResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/${companyId}/history`, {
        params: { page },
        withCredentials: true,
      });

      if (response.data?.success) {
        // APIレスポンスは既にキャメルケースで返ってくるため、そのまま返す
        return response.data as ScoutHistoryResponse;
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error?.message) {
          throw new Error(error.response.data.error.message);
        }
        switch (error.response?.status) {
          case 403:
            throw new Error('スカウト履歴の閲覧権限がありません');
          case 500:
            throw new Error('サーバーエラーが発生しました');
          default:
            throw new Error(
              `API エラー: ${error.response?.status || 'unknown'}`
            );
        }
      }
      throw new Error('スカウト履歴の取得に失敗しました');
    }
  },

  // 送信制限情報取得
  async getQuota(companyId: number = 9): Promise<ScoutQuotaResponse> {
    try {
      const response = await axios.get(`${API_BASE_URL}/${companyId}/quota`, {
        withCredentials: true,
      });

      if (response.data?.success) {
        const backendQuota = response.data.data?.current_usage;
        const normalized = {
          ...response.data,
          data: {
            currentUsage: {
              perDay: backendQuota?.per_day
                ? {
                    used: backendQuota.per_day.used,
                    limit: backendQuota.per_day.limit,
                    resetsAt: backendQuota.per_day.resets_at,
                  }
                : undefined,
              perMonth: backendQuota?.per_month
                ? {
                    used: backendQuota.per_month.used,
                    limit: backendQuota.per_month.limit,
                    resetsAt: backendQuota.per_month.resets_at,
                  }
                : undefined,
            },
            recommendations: response.data.data?.recommendations || [],
            nextAvailableSend: response.data.data?.next_available_send,
          },
        };
        return normalized as ScoutQuotaResponse;
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error?.message) {
          throw new Error(error.response.data.error.message);
        }
        switch (error.response?.status) {
          case 403:
            throw new Error('送信制限情報の閲覧権限がありません');
          case 500:
            throw new Error('サーバーエラーが発生しました');
          default:
            throw new Error(
              `API エラー: ${error.response?.status || 'unknown'}`
            );
        }
      }
      throw new Error('送信制限情報の取得に失敗しました');
    }
  },

  // スカウトメッセージ詳細取得
  async getMessageDetail(
    companyId: number = 9,
    messageId: number
  ): Promise<any> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/${companyId}/messages/${messageId}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error?.message) {
          throw new Error(error.response.data.error.message);
        }
        switch (error.response?.status) {
          case 403:
            throw new Error('スカウトメッセージの閲覧権限がありません');
          case 404:
            throw new Error('スカウトメッセージが見つかりません');
          case 500:
            throw new Error('サーバーエラーが発生しました');
          default:
            throw new Error(
              `API エラー: ${error.response?.status || 'unknown'}`
            );
        }
      }
      throw new Error('スカウトメッセージ詳細の取得に失敗しました');
    }
  },

  // 送信制限数カウント更新
  async updateQuotaUsage(
    senderId: number,
    companyId: number
  ): Promise<{
    today: number;
    this_month: number;
  }> {
    const quota = await this.getScoutQuota(senderId, companyId);

    return {
      today: quota.data.currentUsage.perDay?.used ?? 0,
      this_month: quota.data.currentUsage.perMonth?.used ?? 0,
    };
  },
};
